import React from 'react';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import SettingsIcon from '@mui/icons-material/Settings';
import PermMediaIcon from '@mui/icons-material/PermMedia';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LzCModule, { LzIModule } from '../../Module';
import LzCEntity from '../../Entity';
import LzCPage from '../../Page';
import LzXPageConfigurations from './Configurations';
import LzXPageMedia from './Media';
import { lzFKebabToWordCase } from '@lz/services/String';
import { LzTBase } from '@lz/models/commons/Base';
import LzMCrud from '@lz/models/crud/Crud';
import LzXCrudListing from '@lz/components/crud/Listing';
import LzXCrudAdd from '@lz/components/crud/Add';
import LzXCrudEdit from '@lz/components/crud/Edit';
import LzXCrudView from '@lz/components/crud/View';
import LzMRole from '@lz/models/commons/Role';
import LzMUser from '@lz/models/commons/User';
import lzGRoles from './Roles/config';
import LzXRolesRights from './Roles/Rights';
import lzGUsers from './Users/config';
import LzXUsersRights from './Users/Rights';
import LzSApp from '@lz/app/App';

interface LzPSystemModule {
    ident?: string;
    name?: string;
    baseRoute?: string;
    slug?: string;
    icon?: React.ReactNode;

    noConfig?: boolean;
    noMedia?: boolean;
    noRoles?: boolean;
    noUsers?: boolean;
    configsPage?: React.ReactNode;
    mediaPage?: React.ReactNode;
    roles?: {
        crud?: LzMCrud;
        modelRef?: LzTBase;
        listingComponent?: React.ReactNode;
        addComponent?: React.ReactNode;
        editComponent?: React.ReactNode;
        viewComponent?: React.ReactNode;
        rightsComponent?: React.ReactNode;
    };
    users?: {
        crud?: LzMCrud;
        modelRef?: LzTBase;
        listingComponent?: React.ReactNode;
        addComponent?: React.ReactNode;
        editComponent?: React.ReactNode;
        viewComponent?: React.ReactNode;
        rightsComponent?: React.ReactNode;
    };
}

class LzYSystem extends LzCModule {
    constructor(props: LzPSystemModule) {
        const ident = props?.ident || 'system';
        const name = props?.name || lzFKebabToWordCase(ident);
        const baseRoute = props?.baseRoute || '/';
        const slug = props?.slug || ident;
        const moduleRoute = baseRoute + slug;

        const module = {
            variation: 'Module',
            ident: ident,
            name: name,
            slug: slug,
            icon: <SettingsSuggestIcon />,
            children: [],
        } as LzIModule;

        if (!props.noConfig) {
            const configsPage = props?.configsPage || (
                <LzSApp.WrapperTemplate private screenTitle="Configurations">
                    <LzXPageConfigurations />
                </LzSApp.WrapperTemplate>
            );
            module.children?.push(new LzCPage({ ident: 'configs', name: 'Configurations', icon: <SettingsIcon />, children: configsPage }));
        }

        if (!props.noMedia) {
            const mediaPage = props?.mediaPage || (
                <LzSApp.WrapperTemplate private screenTitle="Media Files">
                    <LzXPageMedia />
                </LzSApp.WrapperTemplate>
            );
            module.children?.push(new LzCPage({ ident: 'media', name: 'Media Files', icon: <PermMediaIcon />, children: mediaPage }));
        }

        if (!props.noRoles) {
            const rolesCrudConfig = props?.roles?.crud || lzGRoles;
            const rolesModelRef = props?.roles?.modelRef || LzMRole;
            const rolesListingPage = props?.roles?.listingComponent || (
                <LzSApp.WrapperTemplate private screenTitle="User Roles">
                    <LzXCrudListing crud={rolesCrudConfig} modelRef={rolesModelRef} />
                </LzSApp.WrapperTemplate>
            );
            const rolesAddPage = props?.roles?.addComponent || (
                <LzSApp.WrapperTemplate private screenTitle="Create a User Role">
                    <LzXCrudAdd crud={rolesCrudConfig} modelRef={rolesModelRef} />
                </LzSApp.WrapperTemplate>
            );
            const rolesEditPage = props?.roles?.editComponent || (
                <LzSApp.WrapperTemplate private screenTitle="Edit a User Role">
                    <LzXCrudEdit crud={rolesCrudConfig} modelRef={rolesModelRef} />
                </LzSApp.WrapperTemplate>
            );
            const rolesViewPage = props?.roles?.viewComponent || (
                <LzSApp.WrapperTemplate private screenTitle="View a User Role">
                    <LzXCrudView crud={rolesCrudConfig} modelRef={rolesModelRef} />
                </LzSApp.WrapperTemplate>
            );
            const rolesRightsPage = props?.roles?.rightsComponent || (
                <LzSApp.WrapperTemplate private screenTitle="User Role Rights">
                    <LzXRolesRights />
                </LzSApp.WrapperTemplate>
            );
            module.children?.push(
                new LzCEntity({
                    ident: 'roles',
                    name: 'User Roles',
                    icon: <FingerprintIcon />,
                    crud: rolesCrudConfig,
                    modelRef: rolesModelRef,
                    listingComponent: rolesListingPage,
                    addComponent: rolesAddPage,
                    editComponent: rolesEditPage,
                    viewComponent: rolesViewPage,
                    routes: [{ path: moduleRoute + '/roles/:id/rights', element: rolesRightsPage }],
                }),
            );
        }

        if (!props.noUsers) {
            const usersCrudConfig = props?.users?.crud || lzGUsers;
            const usersModelRef = props?.users?.modelRef || LzMUser;
            const usersListingPage = props?.users?.listingComponent || (
                <LzSApp.WrapperTemplate private screenTitle="System User">
                    <LzXCrudListing crud={usersCrudConfig} modelRef={usersModelRef} />
                </LzSApp.WrapperTemplate>
            );
            const usersAddPage = props?.users?.addComponent || (
                <LzSApp.WrapperTemplate private screenTitle="Create a System User">
                    <LzXCrudAdd crud={usersCrudConfig} modelRef={usersModelRef} />
                </LzSApp.WrapperTemplate>
            );
            const usersEditPage = props?.users?.editComponent || (
                <LzSApp.WrapperTemplate private screenTitle="Edit a System User">
                    <LzXCrudEdit crud={usersCrudConfig} modelRef={usersModelRef} />
                </LzSApp.WrapperTemplate>
            );
            const usersViewPage = props?.users?.viewComponent || (
                <LzSApp.WrapperTemplate private screenTitle="View a System User">
                    <LzXCrudView crud={usersCrudConfig} modelRef={usersModelRef} />
                </LzSApp.WrapperTemplate>
            );
            const usersRightsPage = props?.users?.rightsComponent || (
                <LzSApp.WrapperTemplate private screenTitle="System User Rights">
                    <LzXUsersRights />
                </LzSApp.WrapperTemplate>
            );
            module.children?.push(
                new LzCEntity({
                    ident: 'users',
                    name: 'System Users',
                    icon: <AdminPanelSettingsIcon />,
                    crud: usersCrudConfig,
                    modelRef: usersModelRef,
                    listingComponent: usersListingPage,
                    addComponent: usersAddPage,
                    editComponent: usersEditPage,
                    viewComponent: usersViewPage,
                    routes: [{ path: moduleRoute + '/users/:id/rights', element: usersRightsPage }],
                }),
            );
        }

        super(module);
    }
}

export default LzYSystem;
